import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Label, Spinner } from 'reactstrap';
import { Alert, FormGroup } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import {
  ICustomLoginService,
  UserRegistrationStateError,
  UserRegistrationState,
  ILimitedUserInformation,
} from 'services/customLoginService';
import { createChangeHandler } from 'services/formUtils';
import stringUtils from 'services/stringUtils';
import {
  FetchLimitedInfo,
  OneUseCodeLogin,
  PasswordLogin,
  SetPassword,
  SubmitButton,
} from 'components';
import './CustomLogin.css';
import avonovaLogo from 'assets/images/avologo.png';
import apiClient, { ApiError } from '../../../clients/ApiClient';
import { ErrorState } from '../../../models/errors';
import OidcClient from 'clients/OidcClient';

const CustomLogin = (props: ICustomLoginProps) => {
  const { search } = useLocation();
  const { t } = useTranslation('translation');
  const navigate = useNavigate();
  const [ssoInProgress, setSsoInProgress] = useState(false);
  const ssoInfoBase64 = useMemo(() => localStorage.getItem('sso.v2'), []);

  const resetPasswordToken = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get('resetPasswordToken');
  }, [search]);

  const verificationStatus = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get('verificationStatus');
  }, [search]);

  const ssoError = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get('error');
  }, [search]);

  const ssoErrorDescription = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get('error_description');
  }, [search]);

  const ssoAuthCode = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get('code');
  }, [search]);

  const ssoState = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get('state');
  }, [search]);

  const [limitedUserInformation, setLimitedUserInformation] = useState<ILimitedUserInformation>({
    state: UserRegistrationState.Unknown,
    authSetupKey: '',
  });

  const [result, setResult] = useState(
    verificationStatus && verificationStatus === 'ok'
      ? 'login.STATUS_MESSAGE_VERIFICATION_SUCCEEDED'
      : resetPasswordToken
        ? 'login.STATUS_MESSAGE_RESET_PASSWORD_INFO'
        : ''
  );
  const [error, setError] = useState<ErrorState>({
    msgKey:
      verificationStatus && verificationStatus === 'failed'
        ? 'login.STATUS_MESSAGE_VERIFICATION_FAILED'
        : verificationStatus && verificationStatus === 'expired'
          ? 'login.STATUS_MESSAGE_VERIFICATION_EXPIRED'
          : '',
  });
  const [email, setEmail] = useState('');

  const setErrorMessage = (message: string, data: any = {}) => {
    setResult('');
    setError({ msgKey: message, data });
  };

  const setStatusMessage = (message: string) => {
    setResult(message);
    setError({ msgKey: '', data: {} });
  };

  const clearMessages = () => {
    setResult('');
    setError({ msgKey: '', data: {} });
  };

  const handleUserRegistrationStateError = (err: any) => {
    Sentry.captureException(err);
    const error = err as UserRegistrationStateError;
    switch (error.state) {
      case UserRegistrationState.CustomerNotMigrated:
        setErrorMessage('login.STATUS_MESSAGE_CUSTOMER_NOT_MIGRATED');
        break;
      case UserRegistrationState.Unknown:
        setErrorMessage('login.STATUS_MESSAGE_USER_NOT_IN_J4');
        break;
      case UserRegistrationState.Verified:
      case UserRegistrationState.Unverified:
        setErrorMessage('login.STATUS_MESSAGE_USER_ALREADY_CREATED');
        break;
      case UserRegistrationState.CustomerMigrationNotFinished:
        setErrorMessage('login.STATUS_MESSAGE_CUSTOMER_MIGRATION_NOT_FINISHED');
        break;
      default:
        setErrorMessage('login.STATUS_MESSAGE_UNEXPECTED_ERROR');
        break;
    }
  };

  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(!!resetPasswordToken);

  const [resetPasswordLinkSent, setResetPasswordLinkSent] = useState(false);

  const [resetTokenRejected, setResetTokenRejected] = useState(false);
  const [requestingNewResetLink, setRequestingNewResetLink] = useState(false);

  const emailIsValid = useMemo(() => stringUtils.validateEmail(email), [email]);

  const onRequestNewVerificationToken = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setStatusMessage('login.STATUS_MESSAGE_SENDING_NEW_VALIDATION_LINK');
    try {
      await props.loginService.RequestNewVerificationToken(email);
      setStatusMessage('login.STATUS_MESSAGE_NEW_VALIDATION_LINK_WAS_SENT');
    } catch (error) {
      console.error(error);
      setErrorMessage('login.STATUS_MESSAGE_NEW_VALIDATION_LINK_COULD_NOT_BE_SENT');
    }
  };

  const onOldLogin = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    event.stopPropagation();
    props.onOldLogin();
  };

  const onRequestResetPasswordToken = async () => {
    setStatusMessage('login.STATUS_MESSAGE_SENDING_PASSWORD_RESET_TOKEN');
    try {
      await props.loginService.RequestResetPasswordToken(email);
      setResetPasswordLinkSent(true);
      setStatusMessage('login.STATUS_MESSAGE_PASSWORD_RESET_TOKEN_WAS_SENT');
      return true;
    } catch (error) {
      console.error(error);
      setErrorMessage('login.STATUS_MESSAGE_COULD_NOT_SEND_PASSWORD_RESET_TOKEN');
      return false;
    }
  };

  const onRequestNewResetLink = async () => {
    setRequestingNewResetLink(true);
    try {
      const sent = await onRequestResetPasswordToken();
      if (sent) navigate('/login', { replace: true });
    } finally {
      setRequestingNewResetLink(false);
    }
  };

  const onPasswordReset = async (_email: string, password: string) => {
    setStatusMessage(t('login.STATUS_MESSAGE_SETTING_PASSWORD'));
    if (!resetPasswordToken) {
      return;
    }
    try {
      const limitedUserInfo = await props.loginService.ResetPassword(password, resetPasswordToken);
      const resolvedEmail = limitedUserInfo.email ?? '';
      // Explicitly tell Chromium's credential manager which account the new password
      // belongs to, so it offers "save/update" instead of saving with an empty username.
      if (
        resolvedEmail &&
        typeof window !== 'undefined' &&
        'PasswordCredential' in window &&
        navigator.credentials?.store
      ) {
        try {
          const credential = new (window as any).PasswordCredential({
            id: resolvedEmail,
            password,
            name: resolvedEmail,
          });
          await navigator.credentials.store(credential);
        } catch (credentialError) {
          console.warn('Could not store credential in password manager', credentialError);
        }
      }
      setEmail(resolvedEmail);
      setLimitedUserInformation({ state: UserRegistrationState.Unknown, authSetupKey: '' });
      setShowResetPasswordDialog(false);
      setStatusMessage('login.STATUS_MESSAGE_PASSWORD_WAS_SET');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error(error);
      const problemTypeName = (error as ApiError)?.problem?.typeName;
      if (problemTypeName === 'InvalidResetToken') {
        setResetTokenRejected(true);
        setErrorMessage('login.problem.InvalidResetToken');
      } else if (problemTypeName === 'PasswordPolicy') {
        setErrorMessage('login.problem.PasswordPolicy');
      } else {
        setErrorMessage('login.STATUS_MESSAGE_COULD_NOT_SET_PASSWORD');
      }
    }
  };

  const ssoSignIn = async () => {
    try {
      if (!ssoInfoBase64) {
        setErrorMessage('login.SSO_NOT_CONFIGURED');
        return;
      }

      const ssoInfo = JSON.parse(atob(ssoInfoBase64));
      const oidcClient = new OidcClient(ssoInfo.openIdConfig, ssoInfo.clientId);
      await oidcClient.authorizeRedirect(ssoInfo.scope ?? '', `${location.origin}/login`);
    } catch (error) {
      console.error(error);
      setErrorMessage('login.SSO_FAILED');
    }
  };

  const onFetchUserInfo = async (email: string) => {
    clearMessages();
    try {
      const limitedUserInfo = await props.loginService.GetLimitedUserInformation(email);
      if (limitedUserInfo.ssoInfo) {
        const oidcClient = new OidcClient(
          limitedUserInfo.ssoInfo.openIdConfig,
          limitedUserInfo.ssoInfo.clientId
        );

        await oidcClient.authorizeRedirect(
          limitedUserInfo.ssoInfo.scope ?? '',
          `${location.origin}/login`
        );

        // Set delay to avoid "blinking" button
        await new Promise((resolve) => setTimeout(resolve, 1000 * 60));
      }

      setLimitedUserInformation(limitedUserInfo);
      if (limitedUserInfo.state === UserRegistrationState.Unregistered)
        setStatusMessage('login.STATUS_MESSAGE_FIRST_LOGIN');
      if (limitedUserInfo.state === UserRegistrationState.Unverified)
        setErrorMessage('login.problem.EmailNotVerified');
    } catch (err) {
      handleUserRegistrationStateError(err);
    }
  };

  const onPasswordSet = async (email: string, password: string) => {
    clearMessages();
    try {
      const limitedUserInfo = await props.loginService.RegisterNewUser(email, password);
      setLimitedUserInformation(limitedUserInfo);
      setStatusMessage('login.STATUS_MESSAGE_REGISTRATION_SUCCEDED');
    } catch (err) {
      handleUserRegistrationStateError(err);
    }
  };

  const onPasswordLogin = async (email: string, password: string) => {
    clearMessages();
    try {
      const tokenResult = await apiClient.getToken({ email, password });
      if (!tokenResult.mfa) {
        console.error('No MFA information in result');
        setErrorMessage('login.ERROR_UNKNOWN_ERROR');
        return;
      }

      setLimitedUserInformation({
        state: UserRegistrationState.AwaitingOneTimeCode,
        authSetupKey: tokenResult.mfa.setupUri,
        sessionId: tokenResult.mfa.sessionId,
      });
    } catch (error) {
      const apiApiError = error as ApiError;
      if (apiApiError.status === 401) {
        setErrorMessage('login.problem.InvalidPassword');
        return;
      }

      if (apiApiError.status === 429) {
        setErrorMessage('login.problem.TooManyRequests');
        return;
      }

      if (apiApiError.problem) {
        Sentry.captureException(error);
        const errorKey = `login.problem.${apiApiError.problem.typeName}`;
        setErrorMessage(errorKey, apiApiError.problem.data);
        return;
      }
      console.error(error);
    }
  };

  const onOneUseCodeLogin = async (code: string) => {
    clearMessages();
    try {
      const sessionId = limitedUserInformation.sessionId;
      const tokenResult = await apiClient.getToken({ sessionId, code });
      if (!tokenResult.token) {
        console.error('Missing token in result');
        setErrorMessage('login.ERROR_UNKNOWN_ERROR');
        return;
      }
      await props.loginService.SetAuthState(
        tokenResult.token.accessToken,
        tokenResult.token.refreshToken
      );
    } catch (error) {
      const apiApiError = error as ApiError;
      if (apiApiError.status === 401) {
        const msgKey = limitedUserInformation.authSetupKey
          ? 'login.problem.InvalidSetupMfaCode'
          : 'login.problem.InvalidMfaCode';

        setErrorMessage(msgKey);
        return;
      }

      if (apiApiError.status === 429) {
        setErrorMessage('login.problem.TooManyRequests');
        return;
      }

      if (apiApiError.problem) {
        Sentry.captureException(error);
        const errorKey = `login.problem.${apiApiError.problem.typeName}`;
        setErrorMessage(errorKey, apiApiError.problem.data);
        return;
      }
      console.error(error);
    }
  };

  const onRestart = () => {
    setLimitedUserInformation({ state: UserRegistrationState.Unknown });
    clearMessages();
  };

  const ssoLogin = async (code: string, state: string) => {
    try {
      const oidcClient = OidcClient.fromState(state);
      if (!oidcClient) return navigate('/login');

      const response = await oidcClient.getToken(code);

      if (response.error) {
        Sentry.captureEvent({
          level: 'error',
          message: 'Failed to get SSO token',
          extra: {
            response,
            authority: oidcClient.getAuthority(),
            clientId: oidcClient.getClientId(),
          },
        });
        console.error('Failed to get SSO token:', response);
        setErrorMessage(`ssoErrors.${response.error}`, { description: response.error_description });
        return navigate('/login');
      }

      if (!response.id_token) {
        setErrorMessage('ssoErrors.token_error');
        return navigate('/login');
      }

      navigate('/login');
      const tokenResult = await apiClient.getToken({
        ssoIdToken: response.id_token,
        ssoAccessToken: response.access_token,
      });

      if (tokenResult.mfa) {
        setLimitedUserInformation({
          state: UserRegistrationState.AwaitingOneTimeCode,
          authSetupKey: tokenResult.mfa.setupUri,
          sessionId: tokenResult.mfa.sessionId,
        });
        return;
      }

      if (tokenResult.token) {
        await props.loginService.SetAuthState(
          tokenResult.token.accessToken,
          tokenResult.token.refreshToken
        );
        return;
      }

      console.error('Missing token in result');
      setErrorMessage('login.ERROR_UNKNOWN_ERROR');
    } catch (error) {
      const apiApiError = error as ApiError;
      if (apiApiError?.problem) {
        Sentry.captureException(apiApiError);
        const errorKey = `login.problem.${apiApiError.problem.typeName}`;
        setErrorMessage(errorKey, apiApiError.problem.data);
        return;
      }

      console.error('Error during SSO login:', error);
      Sentry.captureException(error);
      setErrorMessage('login.ERROR_UNKNOWN_ERROR');
    }
  };

  useEffect(() => {
    if (ssoError) {
      setErrorMessage(`ssoErrors.${ssoError}`, { description: ssoErrorDescription });
      return navigate('/login');
    }

    if (ssoAuthCode && ssoState) {
      setSsoInProgress(true);
      ssoLogin(ssoAuthCode, ssoState)
        .catch((error) => {
          const apiApiError = error as ApiError;
          if (apiApiError.problem) {
            setErrorMessage(`login.problem.${apiApiError.problem.typeName}`);
            return;
          }

          console.error('Error fetching access token:', error);
          setErrorMessage('ssoErrors.token_error');
        })
        .finally(() => setSsoInProgress(false));
    }
  }, []);

  const getFormBasedOnState = () => {
    if (resetPasswordLinkSent) return null;

    if (showResetPasswordDialog) {
      if (resetTokenRejected)
        return (
          <>
            <h4>{t('login.HEADER_RESET_PASSWORD')}</h4>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                onRequestNewResetLink();
              }}
            >
              <Label>
                {t('login.LABEL_EMAIL')}{' '}
                <Input
                  name="email"
                  autoComplete="username"
                  placeholder={t('login.PROMPT_FOR_EMAIL')}
                  value={email}
                  type="text"
                  onChange={createChangeHandler(setEmail)}
                />
              </Label>
              <SubmitButton
                type="submit"
                submitting={requestingNewResetLink}
                disabled={!emailIsValid || requestingNewResetLink}
                label={t('login.SUBMIT_BUTTON_REQUEST_NEW_RESET_LINK')}
                activelabel={t('login.STATUS_MESSAGE_SENDING_PASSWORD_RESET_TOKEN')}
                className="btn-primary"
              />
            </form>
          </>
        );

      return (
        <>
          <h4>{t('login.HEADER_RESET_PASSWORD')}</h4>
          <SetPassword
            onSubmit={onPasswordReset}
            hideEmail
            submitButtonLabel={t('login.SUBMIT_BUTTON_RESET_PASSWORD')}
            submitButtonActiveLabel={t('login.SUBMIT_BUTTON_RESETTING_PASSWORD')}
          />
        </>
      );
    }

    switch (limitedUserInformation.state) {
      case UserRegistrationState.Unknown:
        return (
          <FetchLimitedInfo
            onSubmit={onFetchUserInfo}
            onEmailChange={setEmail}
            onSsoSubmit={ssoSignIn}
            showSsoOption={!!ssoInfoBase64}
          />
        );
      case UserRegistrationState.Unregistered:
        return (
          <SetPassword
            onSubmit={onPasswordSet}
            defaultEmail={email}
            onRestart={onRestart}
            submitButtonLabel={t('login.SUBMIT_BUTTON_CREATE_ACCOUNT')}
            submitButtonActiveLabel={t('login.SUBMIT_BUTTON_CREATING_ACCOUNT')}
          />
        );
      case UserRegistrationState.Unverified:
        break;
      case UserRegistrationState.Verified:
        return (
          <>
            <PasswordLogin
              onSubmit={onPasswordLogin}
              onEmailChange={setEmail}
              defaultEmail={email}
              onRestart={onRestart}
              onRequestResetPasswordToken={async () => {
                await onRequestResetPasswordToken();
              }}
            />
          </>
        );
      case UserRegistrationState.AwaitingOneTimeCode:
        return (
          <OneUseCodeLogin
            onSubmit={onOneUseCodeLogin}
            qrRegistrationUrl={limitedUserInformation.authSetupKey}
            onRestart={onRestart}
          />
        );
    }
  };

  function renderErrorMessage() {
    if (error.msgKey === 'login.STATUS_MESSAGE_CUSTOMER_NOT_MIGRATED')
      return (
        <Trans i18nKey="login.STATUS_MESSAGE_CUSTOMER_NOT_MIGRATED" ns="translation">
          <a href="https://webbportal.avonova.se/" className="requestlink">
            Web portal link
          </a>
        </Trans>
      );

    if (error.msgKey === 'login.STATUS_MESSAGE_CUSTOMER_MIGRATION_NOT_FINISHED')
      return (
        <Trans i18nKey="login.STATUS_MESSAGE_CUSTOMER_MIGRATION_NOT_FINISHED" ns="translation">
          <a href="https://www.avonova.se/hitta-till-oss" className="requestlink">
            Contact details link
          </a>
        </Trans>
      );

    if (error.msgKey === 'login.problem.AccountLocked')
      return (
        <>
          <Trans
            i18nKey="login.problem.AccountLocked"
            ns="translation"
            values={{ minutes: calculateMinutes(error.data.lockedEnd) }}
          ></Trans>
          <div className="accountLockedActions">
            <Button color="link" onClick={onRestart} className="requestlink">
              {t('login.BACK_TO_LOGIN')}
            </Button>
          </div>
        </>
      );

    if (error.msgKey.startsWith('ssoErrors.'))
      return <Trans i18nKey={error.msgKey} ns="translation" values={error.data}></Trans>;

    return (
      <Trans i18nKey={error.msgKey} ns="translation">
        <a
          href="#requestNewVerificationToken"
          className="requestlink"
          onClick={onRequestNewVerificationToken}
        >
          Request new verification link
        </a>
      </Trans>
    );
  }

  return (
    <>
      {!ssoInProgress && (
        <FormGroup>
          {result && (
            <Alert key="success" variant="success">
              {t(result)}
            </Alert>
          )}
          {error.msgKey && (
            <Alert key="danger" variant="danger" className="alert-danger">
              {renderErrorMessage()}
            </Alert>
          )}
          {limitedUserInformation.state !== UserRegistrationState.Unverified && (
            <div className="customLoginContainer">{getFormBasedOnState()}</div>
          )}
          {(limitedUserInformation.state === UserRegistrationState.Unknown ||
            limitedUserInformation.state === UserRegistrationState.Unregistered) &&
            !ssoInfoBase64 && (
              <div>
                <p className="login-info-section">
                  <img src={avonovaLogo} alt="Avonova logo" className="avologo" />
                  <Trans i18nKey="login.INFO_AVONOVA_STAFF" ns="translation">
                    <a href="#loginWithMS" onClick={onOldLogin}>
                      Login with microsoft account
                    </a>
                  </Trans>
                </p>
              </div>
            )}
        </FormGroup>
      )}
      {ssoInProgress && (
        <div className="col-md-6 offset-md-6">
          <Spinner />
        </div>
      )}
    </>
  );
};

function calculateMinutes(utcDate: string) {
  const data = new Date(utcDate);
  const now = new Date();
  const diff = data.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60));
}

export interface ICustomLoginProps {
  loginService: ICustomLoginService;
  onOldLogin: () => void;
}

export default CustomLogin;
